import { getPostHogHost, getPostHogKey } from "~/lib/env";
import { getRouteVisitId } from "~/lib/route-visit";
import { registerTelemetryProvider, trackTelemetryEvent } from "../client";
import type { TelemetryProvider } from "../events";
import type { PostHog, PostHogConfig } from "posthog-js";

interface EnablePostHogTelemetryInput {
  readonly sessionReplayEnabled: boolean;
}

let posthogClient: PostHog | null = null;
let posthogInitPromise: Promise<PostHog | null> | null = null;
let lastSessionReplayEnabled: boolean | null = null;
let hasTrackedPostHogEnabled = false;

function configureSessionReplay(
  client: PostHog,
  sessionReplayEnabled: boolean,
): void {
  if (lastSessionReplayEnabled === sessionReplayEnabled) {
    return;
  }

  lastSessionReplayEnabled = sessionReplayEnabled;

  if (sessionReplayEnabled) {
    client.startSessionRecording({
      linked_flag: true,
      sampling: true,
    });
    return;
  }

  client.stopSessionRecording();
}

export function createPostHogTelemetryProvider(
  client: Pick<PostHog, "capture" | "identify" | "reset">,
): TelemetryProvider {
  return {
    name: "posthog",
    identify(identity) {
      if (identity.authenticated && identity.canonicalUserId) {
        client.identify(identity.canonicalUserId, {
          auth_source: identity.authSource,
          authenticated: identity.authenticated,
          ...(identity.privyUserId ? { privy_id: identity.privyUserId } : {}),
          ...(identity.walletAddress
            ? { wallet_address: identity.walletAddress }
            : {}),
        });
        return;
      }

      client.reset(false);
    },
    reset() {
      client.reset(false);
    },
    track(event) {
      client.capture(event.name, event.properties);
    },
  };
}

async function initializePostHog(input: {
  apiHost: string;
  apiKey: string;
  sessionReplayEnabled: boolean;
}): Promise<PostHog | null> {
  if (typeof window === "undefined") {
    return null;
  }

  const { default: posthog } = await import("posthog-js");
  const config = {
    api_host: input.apiHost,
    autocapture: false,
    capture_exceptions: false,
    capture_pageleave: false,
    capture_pageview: false,
    disable_session_recording: !input.sessionReplayEnabled,
    mask_all_element_attributes: true,
    mask_all_text: true,
    person_profiles: "identified_only",
  } satisfies Partial<PostHogConfig>;

  const client = posthog.init(input.apiKey, config);
  if (!client) {
    return null;
  }

  posthogClient = client;
  registerTelemetryProvider(createPostHogTelemetryProvider(client));
  configureSessionReplay(client, input.sessionReplayEnabled);

  if (!hasTrackedPostHogEnabled) {
    hasTrackedPostHogEnabled = true;
    trackTelemetryEvent("telemetry.provider.enabled", {
      provider: "posthog",
      route_visit_id: getRouteVisitId(),
      session_replay_enabled: input.sessionReplayEnabled,
    });
  }

  return client;
}

export function enablePostHogTelemetry(
  input: EnablePostHogTelemetryInput,
): void {
  const apiKey = getPostHogKey();
  if (!apiKey) {
    return;
  }

  if (posthogClient) {
    configureSessionReplay(posthogClient, input.sessionReplayEnabled);
    return;
  }

  posthogInitPromise ??= initializePostHog({
    apiHost: getPostHogHost(),
    apiKey,
    sessionReplayEnabled: input.sessionReplayEnabled,
  });

  void posthogInitPromise.then((client) => {
    if (client) {
      configureSessionReplay(client, input.sessionReplayEnabled);
    }
  });
}

export function resetPostHogTelemetryForTests(): void {
  posthogClient = null;
  posthogInitPromise = null;
  lastSessionReplayEnabled = null;
  hasTrackedPostHogEnabled = false;
}
